import {HttpErrorResponse} from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  TemplateRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {ButtonModule} from 'primeng/button';
import {InputTextModule} from 'primeng/inputtext';
import {SelectModule} from 'primeng/select';
import {map} from 'rxjs';

import {SUPPORTED_LANGUAGES, SupportedLanguage, TLabel, TranslatePipe} from '@mydeals/i18n';
import {AuthStore} from '@core/auth';
import {ProblemDetailsResponse} from '@core/models';
import {Toast} from '@core/services';
import {TenantStore} from '@core/tenant';
import type {AvatarCropData, AvatarCropModal} from '@shared/ui/avatar-crop-modal';
import {AvatarField} from '@shared/ui/avatar-field';
import {DrawerRef} from '@shared/ui/drawer';
import {ModalService} from '@shared/ui/modal';
import {PhoneInput} from '@shared/ui/phone-input';
import {PhoneValue, applyServerFieldErrors, e164FromPhoneValue, isValidPhone, phoneValueFromE164} from '@shared/utils';

interface LanguageOption {
  readonly value: SupportedLanguage;
  readonly labelKey: TLabel;
}

interface ProfileFormModel {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  phone: FormControl<PhoneValue>;
  language: FormControl<SupportedLanguage>;
}

const LANGUAGE_OPTIONS: LanguageOption[] = SUPPORTED_LANGUAGES.map((lang) => ({
  value: lang,
  labelKey: `profile.language.${lang}` as TLabel,
}));

const DEFAULT_COUNTRY = 'UA';
const MAX_AVATAR_BYTES = 5 * 1024 * 1024;
const ALLOWED_AVATAR_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export interface ProfileFormResult {
  readonly saved: boolean;
}

/** Control validator: a non-empty number must be valid for the chosen country. */
function phoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as PhoneValue;
    const number = (value?.number ?? '').trim();
    if (!number) return null;
    return isValidPhone(number, value.countryIso2) ? null : {invalidPhone: true};
  };
}

@Component({
  selector: 'crm-profile-form',
  imports: [ReactiveFormsModule, InputTextModule, SelectModule, ButtonModule, PhoneInput, AvatarField, TranslatePipe],
  templateUrl: './profile-form.html',
  styleUrl: './profile-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileForm implements AfterViewInit {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly authStore = inject(AuthStore);
  private readonly tenant = inject(TenantStore);
  private readonly drawerRef = inject<DrawerRef<ProfileFormResult>>(DrawerRef);
  private readonly modal = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(Toast);

  @ViewChild('footerTpl', {static: true}) footerTpl!: TemplateRef<unknown>;

  readonly languageOptions = LANGUAGE_OPTIONS;
  readonly isSaving = signal(false);

  private readonly currentUser = this.authStore.currentUser;
  readonly email = computed(() => this.currentUser()?.email ?? '');
  readonly fullName = computed(() => {
    const user = this.currentUser();
    return user ? `${user.firstName} ${user.lastName}`.trim() : '';
  });

  private readonly stagedFile = signal<File | null>(null);
  private readonly previewUrl = signal<string | null>(null);
  private readonly removed = signal(false);
  readonly avatarError = signal<TLabel | null>(null);

  private readonly backendAvatarUrl = computed(() => this.currentUser()?.avatarUrl ?? null);
  readonly avatarUrl = computed(() => this.previewUrl() ?? (this.removed() ? null : this.backendAvatarUrl()));
  readonly canReset = computed(() => this.stagedFile() !== null || this.removed());
  readonly canDelete = computed(() => this.backendAvatarUrl() !== null);

  readonly form: FormGroup<ProfileFormModel> = this.fb.group({
    firstName: this.fb.control(this.currentUser()?.firstName ?? '', [Validators.required, Validators.minLength(2)]),
    lastName: this.fb.control(this.currentUser()?.lastName ?? '', [Validators.required, Validators.minLength(2)]),
    phone: this.fb.control<PhoneValue>(this.initialPhone(), phoneValidator()),
    language: this.fb.control<SupportedLanguage>(
      (this.currentUser()?.language ?? 'uk') as SupportedLanguage,
      Validators.required,
    ),
  });

  readonly firstNameHasError = toSignal(
    this.form.controls.firstName.events.pipe(
      map(() => this.form.controls.firstName.invalid && this.form.controls.firstName.touched),
    ),
    {initialValue: false},
  );
  readonly lastNameHasError = toSignal(
    this.form.controls.lastName.events.pipe(
      map(() => this.form.controls.lastName.invalid && this.form.controls.lastName.touched),
    ),
    {initialValue: false},
  );
  readonly phoneHasError = toSignal(
    this.form.controls.phone.events.pipe(
      map(() => this.form.controls.phone.invalid && this.form.controls.phone.touched),
    ),
    {initialValue: false},
  );

  constructor() {
    this.destroyRef.onDestroy(() => this.revokePreview());
  }

  ngAfterViewInit(): void {
    this.drawerRef.setFooter(this.footerTpl);
  }

  async onAvatarPicked(file: File): Promise<void> {
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      this.avatarError.set('common.avatar.error.type');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      this.avatarError.set('common.avatar.error.size');
      return;
    }
    this.avatarError.set(null);

    const {AvatarCropModal} = await import('@shared/ui/avatar-crop-modal');
    this.modal
      .open<AvatarCropModal, File, AvatarCropData>(AvatarCropModal, {
        title: 'common.avatar.crop.title',
        size: 'md',
        data: {file},
      })
      .closed$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cropped) => {
        if (cropped) this.stageAvatar(cropped);
      });
  }

  private stageAvatar(file: File): void {
    this.revokePreview();
    this.stagedFile.set(file);
    this.previewUrl.set(URL.createObjectURL(file));
    this.removed.set(false);
  }

  onAvatarReset(): void {
    this.revokePreview();
    this.stagedFile.set(null);
    this.previewUrl.set(null);
    this.removed.set(false);
    this.avatarError.set(null);
  }

  onAvatarDeleted(): void {
    this.revokePreview();
    this.stagedFile.set(null);
    this.previewUrl.set(null);
    this.removed.set(true);
    this.avatarError.set(null);
  }

  cancel(): void {
    this.drawerRef.close();
  }

  async openChangePassword(): Promise<void> {
    const {openChangePasswordModal} = await import('./components/change-password-modal');
    openChangePasswordModal(this.modal);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toast.warn('validation.invalidForm');
      return;
    }

    this.isSaving.set(true);
    const {firstName, lastName, phone, language} = this.form.getRawValue();

    this.authStore
      .updateMe({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: e164FromPhoneValue(phone),
        language,
        avatar: this.stagedFile(),
        removeAvatar: this.removed(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toast.success('profile.success');
          this.drawerRef.close({saved: true});
        },
        error: (httpError: HttpErrorResponse) => {
          this.isSaving.set(false);
          const fields = (httpError.error as ProblemDetailsResponse)?.fields;
          if (fields?.includes('avatar')) this.avatarError.set('common.avatar.error.invalid');
          applyServerFieldErrors(this.form, fields, this.destroyRef);
        },
      });
  }

  /** GET /me never returns `phone`, so this always starts empty — see `UpdateMeRequest`. */
  private initialPhone(): PhoneValue {
    const fallback = this.tenant.settings()?.defaultCountryCode ?? DEFAULT_COUNTRY;
    return phoneValueFromE164(null, fallback);
  }

  private revokePreview(): void {
    const url = this.previewUrl();
    if (url) URL.revokeObjectURL(url);
  }
}
